// App-wide font-size accessibility preference
// (normal / large / x-large / xx-large / xxx-large).
// A real Context (not just a localStorage-backed hook) so that cycling the
// toggle in AppLayout's header is immediately reflected everywhere else that
// cares about the current scale - e.g. checklist/list/template pages sizing
// their checkboxes/chevrons/edit icons to match the scaled text. Persistence
// lives in lib/displayPrefs.ts.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  applyFontScale,
  getFontScale,
  nextFontScale,
  setFontScale,
  type FontScale,
} from "../lib/displayPrefs.js";

interface FontScaleContextValue {
  scale: FontScale;
  cycleScale: () => void;
}

const FontScaleContext = createContext<FontScaleContextValue | null>(null);

export function FontScaleProvider({ children }: { children: ReactNode }) {
  const [scale, setScale] = useState<FontScale>(() => getFontScale());

  // main.tsx already applies the persisted scale before first paint to avoid
  // a flash of unscaled text; this keeps the DOM attribute in sync on every
  // subsequent change (including from other instances via cycleScale below).
  useEffect(() => {
    applyFontScale(scale);
  }, [scale]);

  const cycleScale = useCallback(() => {
    setScale((current) => {
      const next = nextFontScale(current);
      setFontScale(next);
      return next;
    });
  }, []);

  const value = useMemo<FontScaleContextValue>(() => ({ scale, cycleScale }), [scale, cycleScale]);

  return <FontScaleContext.Provider value={value}>{children}</FontScaleContext.Provider>;
}

export function useFontScale(): FontScaleContextValue {
  const ctx = useContext(FontScaleContext);
  if (!ctx) throw new Error("useFontScale must be used within FontScaleProvider");
  return ctx;
}
